import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { filterAndSort, sortArr, money, buildServicioNombre } from "@/lib/helpers";
import { exportExcel, exportPDF } from "@/lib/exports";
import PaginationBar from "@/components/PaginationBar";

const blank = {
  codigo: "",
  nombre: "",
  descripcion: "",
  precio: 0,
  mes: "",
  incidencia: "",
  sede: "",
};

export default function Servicios({ state, upsert, remove }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "nombre", dir: 1 });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(blank);
  const [manualName, setManualName] = useState(false);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);

  const toggleSort = (field) =>
    setSort((s) => (s.field === field ? { field, dir: -s.dir } : { field, dir: 1 }));

  const allRows = useMemo(
    () => filterAndSort(state.servicios, search, sort, state),
    [state.servicios, search, sort, state]
  );
  const pagedRows = allRows.slice(skip, skip + limit);

  const openModal = (row = null) => {
    const data = row ? { ...blank, ...row } : { ...blank };
    setForm(data);
    // If editing, enable manual mode so we don't overwrite existing name unexpectedly
    setManualName(!!row);
    setModal(true);
  };

  // Auto-generated name preview from fields
  const autoName = useMemo(
    () => buildServicioNombre({ mes: form.mes, incidencia: form.incidencia, sede: form.sede }),
    [form.mes, form.incidencia, form.sede]
  );

  const previewName = manualName ? form.nombre : autoName || form.nombre;

  const save = async (e) => {
    e.preventDefault();
    // Require incidencia
    if (!form.incidencia || !String(form.incidencia).trim()) {
      toast.error("El número de incidencia es obligatorio");
      return;
    }
    const finalName = manualName
      ? (form.nombre || autoName)
      : (autoName || form.nombre);
    if (!finalName) {
      toast.error("Falta el nombre del servicio");
      return;
    }
    await upsert("servicios", {
      ...form,
      nombre: finalName,
      precio: +form.precio || 0,
    });
    toast.success("Servicio guardado");
    setModal(false);
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap gap-2 items-center">
        <input className="input max-w-xs" placeholder="Buscar servicio..."
               value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="search-servicios"/>
        <div className="flex-1"/>
        <button className="btn btn-ghost" onClick={() => exportExcel("servicios", state) || toast.error("Sin datos")}>Excel</button>
        <button className="btn btn-ghost" onClick={() => exportPDF("servicios", state) || toast.error("Sin datos")}>PDF</button>
        <button className="btn btn-primary" onClick={() => openModal()} data-testid="btn-new-servicio">+ Nuevo servicio</button>
      </div>

      <div className="card overflow-hidden">
        <table className="data">
          <thead><tr>
            {["codigo", "nombre", "descripcion", "precio"].map((f) => (
              <th key={f} className={`sortable ${f === "precio" ? "text-right " : ""}${sort.field === f ? "active" : ""}`}
                  onClick={() => toggleSort(f)}>
                {({codigo:"Código",nombre:"Nombre",descripcion:"Descripción",precio:"Precio"})[f]}
                <span className="arr">{sortArr(sort, f)}</span>
              </th>
            ))}
            <th/>
          </tr></thead>
          <tbody>
            {pagedRows.map((s) => (
              <tr key={s.id}>
                <td className="font-mono text-xs">{s.codigo}</td>
                <td className="font-semibold">{s.nombre}</td>
                <td className="text-slate-600">{s.descripcion}</td>
                <td className="text-right font-semibold">{money(s.precio)}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="btn btn-ghost !py-1 !px-2" onClick={() => openModal(s)}>Editar</button>
                  <button className="btn btn-danger !py-1 !px-2" onClick={() => remove("servicios", s.id)}>✕</button>
                </td>
              </tr>
            ))}
            {allRows.length === 0 && (
              <tr><td colSpan="5" className="text-center text-slate-400 py-6">No hay servicios</td></tr>
            )}
          </tbody>
        </table>
        <PaginationBar
          total={allRows.length}
          skip={skip}
          limit={limit}
          onSkipChange={setSkip}
          onLimitChange={setLimit}
        />
      </div>

      {modal && (
        <div className="modal-backdrop">
          <div className="modal p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{form.id ? "Editar" : "Nuevo"} servicio</h3>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-700 text-2xl">✕</button>
            </div>
            <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
              {/* New helper fields for auto-naming */}
              <div className="md:col-span-2 p-4 rounded-xl border border-orange-200 bg-orange-50/50">
                <div className="text-xs font-bold text-[var(--cvt-orange)] uppercase tracking-wide mb-3">
                  Generador de nombre de servicio
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="label">Mes (3 dígitos)</label>
                    <input
                      className="input"
                      maxLength={3}
                      placeholder="ej. 04"
                      value={form.mes || ""}
                      onChange={(e) => setForm({ ...form, mes: e.target.value })}
                      data-testid="servicio-mes"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">Opcional · se añade como prefijo con guion bajo</div>
                  </div>
                  <div>
                    <label className="label">Nº Incidencia *</label>
                    <input
                      className="input"
                      required
                      placeholder="ej. INC-20260209-132951"
                      value={form.incidencia || ""}
                      onChange={(e) => setForm({ ...form, incidencia: e.target.value })}
                      data-testid="servicio-incidencia"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">Obligatorio</div>
                  </div>
                  <div>
                    <label className="label">Sede</label>
                    <input
                      className="input"
                      placeholder="ej. 123"
                      value={form.sede || ""}
                      onChange={(e) => setForm({ ...form, sede: e.target.value })}
                      data-testid="servicio-sede"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">Opcional · se añade como " SEDE {"{valor}"}"</div>
                  </div>
                </div>
                <div className="mt-3 p-3 rounded-lg bg-white border border-slate-200">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    Nombre final (vista previa)
                  </div>
                  <div className="font-bold text-base text-[var(--cvt-ink)] break-all" data-testid="servicio-preview">
                    {previewName || <span className="text-slate-400 font-normal">Introduce al menos el nº de incidencia</span>}
                  </div>
                  <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={manualName} onChange={(e) => setManualName(e.target.checked)}/>
                    Editar nombre manualmente
                  </label>
                </div>
              </div>

              <div><label className="label">Código</label>
                <input className="input" value={form.codigo || ""} onChange={(e) => setForm({ ...form, codigo: e.target.value })}/></div>

              <div>
                <label className="label">Nombre final{manualName ? " *" : " (auto)"}</label>
                <input
                  className="input"
                  value={manualName ? (form.nombre || "") : autoName}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  disabled={!manualName}
                  data-testid="servicio-nombre"
                />
              </div>

              <div className="md:col-span-2"><label className="label">Descripción</label>
                <input className="input" value={form.descripcion || ""} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}/></div>

              <div><label className="label">Precio (€) *</label>
                <input className="input" type="number" step="0.01" required value={form.precio} onChange={(e) => setForm({ ...form, precio: e.target.value })} data-testid="servicio-precio"/></div>

              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" data-testid="servicio-save">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
